import React, { useState, useEffect, useMemo } from 'react';
import theme from './ProjectSettingsFeatures.module.scss';
import { Trans } from '@lingui/react/macro';
import { useAsyncState, useIsMobile } from '@/utils/hooks';
import { getProjectFeatures, saveProjectFeatures } from '@/services/projects';
import { ValidationForm } from '@/components/forms/validation';
import {
    ProjectFeatureMode,
    ProjectFeatureConfiguration,
    ProjectFeature,
    EffectiveProjectFeatureConfiguration,
} from '@/types';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import BuyerSidebar from '@/buyer/components/BuyerSidebar';
import { BuyerSidebarLinks } from '@/buyer/components/BuyerSidebar/BuyerSidebar';
import SettingsSection from '../SettingsSection';
import { getResidentialUnitsByProjectId } from '@/services/residential-units';
import { getResidentialUnitGroupsByProjectId } from '@/services/residential-unit-groups';
import { uniq } from 'lodash-es';
import { AlertBanner, Button, ButtonBar, rucoriaTheme, Tabs, useThemeStyle } from '@ygtechnologies/rubus-ui';

const PROJECT_GROUP_ID = 'project';
const ALL_FLOORS_ID = 'all';
import { hasConfig } from '@/services/booking';
import {
    useProjectHasFeatureToggle,
    ProjectFeatureToggleFlags,
    Toast,
    Select,
} from '@ygtechnologies/bygr-frontend-component-library';
import { useLingui } from '@lingui/react';

type ProjectSettingsFeaturesProps = {
    projectId: number;
};

const features: ProjectFeature[] = BuyerSidebarLinks.map((x) => x.featureType);
type FeatureModeMap = { [key in ProjectFeature]?: ProjectFeatureMode };
function calculateEffectiveModes(
    featureConfigs: ProjectFeatureConfiguration[],
    residentialUnitGroupId: number | null,
    floor: string | null
) {
    const result: FeatureModeMap = features.reduce((acc, val) => {
        acc[val] = ProjectFeatureMode.Enabled;
        return acc;
    }, {});

    function apply(items: ProjectFeatureConfiguration[]) {
        items.forEach((item) => {
            if (item.mode !== ProjectFeatureMode.Unset) {
                result[item.feature] = item.mode;
            }
        });
    }

    apply(featureConfigs.filter((x) => x.residentialUnitGroupId === null && !x.floor));

    if (residentialUnitGroupId !== null) {
        apply(featureConfigs.filter((x) => x.residentialUnitGroupId === residentialUnitGroupId && !x.floor));
    }

    if (residentialUnitGroupId && floor) {
        apply(featureConfigs.filter((x) => x.residentialUnitGroupId === residentialUnitGroupId && x.floor === floor));
    }

    return result;
}

const ProjectSettingsFeatures: React.FC<ProjectSettingsFeaturesProps> = ({ projectId }) => {
    const { _ } = useLingui();

    const [featureConfigs, setFeatureConfigs] = useAsyncState(() => getProjectFeatures(projectId), [projectId], []);

    const [residentialUnitGroups] = useAsyncState(() => getResidentialUnitGroupsByProjectId(projectId), [projectId], null);
    const [residentialUnits] = useAsyncState(() => getResidentialUnitsByProjectId(projectId), [projectId], null);

    const [selectedUnitGroupId, setSelectedUnitGroupId] = useState<number | null>(null);
    const [selectedFloor, setSelectedFloor] = useState<string | null>(null);

    const floors = useMemo(() => {
        if (!residentialUnits) {
            return [];
        }

        return uniq(
            residentialUnits.filter((x) => x.residentialUnitGroupId === selectedUnitGroupId && !!x.floor).map((x) => x.floor)
        );
    }, [residentialUnits, selectedUnitGroupId]);

    useEffect(() => {
        setSelectedFloor((value) => {
            if (!selectedUnitGroupId) {
                return null;
            }

            if (value && !floors.includes(value)) {
                return null;
            }

            return value;
        });
    }, [selectedUnitGroupId, floors]);

    const showMeetings = useProjectHasFeatureToggle(ProjectFeatureToggleFlags.ShowMeetings);
    const [hasMeetings] = useAsyncState(async () => await hasConfig(projectId), [projectId], false);

    const data = useMemo(() => {
        const relevantConfigs = featureConfigs.filter(
            (x) => x.residentialUnitGroupId === selectedUnitGroupId && x.floor === selectedFloor
        );

        let defaultMode = ProjectFeatureMode.Enabled;

        if (selectedUnitGroupId || selectedFloor) {
            defaultMode = ProjectFeatureMode.Unset;
        }

        const buyerFeatures = BuyerSidebarLinks.filter((link) =>
            hasMeetings || showMeetings ? true : link.featureType !== ProjectFeature.Meetings
        ).map((x) => x.featureType);

        return buyerFeatures.map((feature) => ({
            feature,
            mode: relevantConfigs.find((conf) => conf.feature === feature)?.mode ?? defaultMode,
        }));
    }, [featureConfigs, selectedUnitGroupId, selectedFloor, hasMeetings, showMeetings]);

    const previewFeatures = useMemo(() => {
        const effectiveModes = calculateEffectiveModes(featureConfigs, selectedUnitGroupId, selectedFloor);

        return features.map<EffectiveProjectFeatureConfiguration>((x) => {
            return {
                feature: x,
                mode: effectiveModes[x]!,
            };
        });
    }, [featureConfigs, selectedUnitGroupId, selectedFloor]);

    const [isSaving, setIsSaving] = useState(false);
    const isMobile = useIsMobile();
    const { styles } = useThemeStyle(rucoriaTheme);
    const onSave = async () => {
        setIsSaving(true);

        try {
            await saveProjectFeatures(projectId, featureConfigs);
            Toast.success(<Trans>Saved features</Trans>);
        } finally {
            setIsSaving(false);
        }
    };

    const handleModeChange = (feature: ProjectFeature, newMode: ProjectFeatureMode) => {
        setFeatureConfigs((oldValue) => {
            const item = oldValue.find(
                (x) => x.residentialUnitGroupId === selectedUnitGroupId && x.floor === selectedFloor && x.feature === feature
            );

            if (item) {
                return arrayReplace(oldValue, item, { ...item, mode: newMode });
            } else {
                return [
                    ...oldValue,
                    {
                        feature,
                        residentialUnitGroupId: selectedUnitGroupId,
                        floor: selectedFloor,
                        mode: newMode,
                    },
                ];
            }
        });
    };

    const options = useMemo(() => {
        let modes = Object.values(ProjectFeatureMode);
        if (selectedUnitGroupId === null) {
            modes = modes.filter((x) => x !== ProjectFeatureMode.Unset);
        }

        return modes.map((x) => ({
            label: isMobile ? _({ id: `ProjectFeatureModeShort.${x}` }) : _({ id: `ProjectFeatureMode.${x}` }),
            value: x,
        }));
    }, [selectedUnitGroupId, isMobile]);

    return (
        <>
            <AlertBanner type="info">
                <Trans>
                    This is where you can enable and disable functionality that the buyers of this project will be exposed
                    to. Use hidden to completely hide functionality. Use deactivated to disable functionality that will later
                    be available.
                </Trans>
            </AlertBanner>
            <SettingsSection>
                <SettingsSection.Content>
                    <ValidationForm onSubmit={onSave}>
                        <div className={theme.container}>
                            <div>
                                <div className={theme.selector}>
                                    <label>
                                        <Trans>Context</Trans>:
                                    </label>
                                    <div>
                                        <Tabs
                                            className={theme.buttonRow}
                                            activeId={selectedUnitGroupId === null ? PROJECT_GROUP_ID : String(selectedUnitGroupId)}
                                            onChange={(id) =>
                                                setSelectedUnitGroupId(id === PROJECT_GROUP_ID ? null : Number(id))
                                            }
                                            variant="pill"
                                            size="sm">
                                            <Tabs.Tab id={PROJECT_GROUP_ID} label={<Trans>Entire project</Trans>} />
                                            {(residentialUnitGroups || []).map((x) => (
                                                <Tabs.Tab key={x.id} id={String(x.id)} label={x.name} />
                                            ))}
                                        </Tabs>

                                        {selectedUnitGroupId && residentialUnits && (
                                            <Tabs
                                                className={theme.buttonRow}
                                                activeId={selectedFloor ?? ALL_FLOORS_ID}
                                                onChange={(id) => setSelectedFloor(id === ALL_FLOORS_ID ? null : id)}
                                                variant="pill"
                                                size="sm">
                                                <Tabs.Tab id={ALL_FLOORS_ID} label={<Trans>All floors</Trans>} />
                                                {floors.map((x) => (
                                                    <Tabs.Tab key={x} id={x} label={x} />
                                                ))}
                                            </Tabs>
                                        )}
                                    </div>
                                </div>
                                <hr />

                                <div className="form columns-1 labels-20">
                                    <div className={theme.headers}>
                                        <label>
                                            <h5>
                                                <Trans>Feature</Trans>
                                            </h5>
                                        </label>
                                        {!isMobile && (
                                            <h5 className={theme.paddingLeft}>
                                                <Trans>Status</Trans>
                                            </h5>
                                        )}
                                    </div>

                                    {data.map((x) => (
                                        <div key={x.feature}>
                                            <label>
                                                {_({ id: `ProjectFeature.${x.feature}` })}
                                                <Select
                                                    className="form-control-unstyled"
                                                    onChange={(nv: any) => handleModeChange(x.feature, nv.value)}
                                                    value={options.find((o) => o.value === x.mode)}
                                                    options={options}
                                                />
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={theme.previewSidebar} style={styles}>
                                <h5>
                                    <Trans>Preview</Trans>
                                </h5>
                                <BuyerSidebar previewFeatures={previewFeatures} previewProjectId={projectId} />
                                {/* <BuyerSidebarDesktop links={links} preview /> */}
                            </div>
                        </div>

                        <ButtonBar>
                            <ButtonBar.Right>
                                <Button type="submit" variant="primary" loading={isSaving}>
                                    <Trans>Save</Trans>
                                </Button>
                            </ButtonBar.Right>
                        </ButtonBar>
                    </ValidationForm>
                </SettingsSection.Content>
            </SettingsSection>
        </>
    );
};

export default ProjectSettingsFeatures;
